import { createApi } from "@reduxjs/toolkit/query/react";
import { apiBaseQuery } from "../utils/apiBaseQuery";
import { CertificateInterface } from "../interfaces/CertificateInterface";
import { MetaInterface } from "../interfaces/MetaInterface";
import { ApiEnum } from "../enums/ApiEnum";
import { createQueryString } from "../utils/createQueryString";

export const certificateApiSlice = createApi({
    reducerPath: "certificateApiSlice",
    baseQuery: apiBaseQuery(),
    tagTypes: ["Certificate"],
    endpoints: (builder) => ({
        getCertificates: builder.query<
            { data: CertificateInterface[]; meta?: MetaInterface },
            {
                page?: number;
                items_per_page?: number;
                sort?: string;
                filter?: {
                    achieved?: boolean;
                    approved?: boolean | null;
                    establishment_id?: number;
                    employee_id?: number;
                    qualification_id?: number;
                    search?: string;
                    status?: string;
                    expiring_soon?: boolean;
                };
            }
        >({
            query: (values) => {
                return {
                    url: `${ApiEnum.CERTIFICATE}?${createQueryString(values)}`,
                    method: "GET",
                };
            },
            providesTags: ["Certificate"],
        }),
        getCertificateById: builder.query<CertificateInterface, { id: number }>(
            {
                query: (values) => {
                    return {
                        url: `${ApiEnum.CERTIFICATE}/${values.id}`,
                        method: "GET",
                    };
                },
                providesTags: ["Certificate"],
            },
        ),
        createCertificate: builder.mutation<
            { data: CertificateInterface },
            {
                data: object;
            }
        >({
            query: (values) => ({
                url: ApiEnum.CERTIFICATE,
                method: "POST",
                body: values.data,
            }),
            invalidatesTags: ["Certificate"],
        }),
        patchCertificate: builder.mutation<
            { data: CertificateInterface },
            {
                id: number;
                data: object;
            }
        >({
            query: (values) => ({
                url: ApiEnum.CERTIFICATE + `/${values.id}`,
                method: "PATCH",
                body: values.data,
            }),
            invalidatesTags: ["Certificate"],
        }),
        getMultipleCertificatesExport: builder.query<
            Blob,
            {
                filter?: {
                    employee_id: number;
                };
            }
        >({
            query: (values) => ({
                url:
                    ApiEnum.CERTIFICATE +
                    `/download-all?${createQueryString(values)}`,
                method: "GET",
                responseHandler: (response) => response.blob(),
            }),
        }),
        getCertificateExport: builder.query<
            Blob,
            {
                url: string;
            }
        >({
            query: (values) => ({
                url: values.url,
                method: "GET",
                responseHandler: (response) => response.blob(),
            }),
        }),
    }),
});

export default certificateApiSlice.reducer;

export const {
    useGetCertificatesQuery,
    useGetCertificateByIdQuery,
    useCreateCertificateMutation,
    usePatchCertificateMutation,
    useGetMultipleCertificatesExportQuery,
    useGetCertificateExportQuery,
} = certificateApiSlice;
